import React, { useState, useEffect } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest, acquireTokenSilent } from "../authConfig";
import axios from "axios";
const API = "https://erp-xe9u.onrender.com/api";

const OpportunitySharePointFilesTab = ({
  entityId,
  entityName,
  entityType, // "Lead" or "Opportunity"
  sharePointFolderName,
  setEntity,
  createdOn, // Assume this prop is passed with the opportunity's creation date
}) => {
  const { instance: msalInstance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [folderCreated, setFolderCreated] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [sharePointConfig, setSharePointConfig] = useState(null);

  const documentLibrary = "Shared Documents";
  const parentFolder = entityType === "Lead" ? "Opportunity" : "Opportunity"; // Same parent folder for consistency

  const fetchSharePointConfig = async (accessToken) => {
    try {
      const rootSiteUrl = "https://graph.microsoft.com/v1.0/sites/root";
      const rootSiteResponse = await axios.get(rootSiteUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const tenant = rootSiteResponse.data.webUrl.split("/")[2];
      const token = localStorage.getItem("token");
      const companyResponse = await axios.get(`${API}/company-profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const siteName = companyResponse.data.sharePointSite || "";
      const sitePath = siteName ? `/sites/${siteName}` : "";

      if (!sitePath) {
        throw new Error(
          "SharePoint site path not configured in company profile.",
        );
      }

      const config = {
        tenant,
        sitePath,
        documentLibrary,
        parentFolder,
      };
      return config;
    } catch (err) {
      console.error("Error fetching SharePoint config:", err);
      throw err;
    }
  };

  const saveFolderName = async (entityId, folderName) => {
    const token = localStorage.getItem("token");
    const endpoint =
      entityType === "Lead" ? `leads/${entityId}` : `opportunities/${entityId}`;
    try {
      const response = await axios.put(
        `${API}/${endpoint}`,
        { sharePointFolderName: folderName },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      console.log(
        `Saved SharePoint folder name: ${folderName} for ${entityType} ${entityId}`,
      );
      return response.data;
    } catch (err) {
      console.error("Error saving folder name:", err.response?.data || err);
      throw err;
    }
  };

  const getSiteId = async (accessToken, config) => {
    const siteUrl = `https://graph.microsoft.com/v1.0/sites/${config.tenant}:${config.sitePath}`;
    const response = await axios.get(siteUrl, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data.id;
  };

  const getDriveId = async (accessToken, siteId) => {
    const driveUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives`;
    const response = await axios.get(driveUrl, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const drives = response.data.value;
    const documentDrive = drives.find((drive) => drive.name === "Documents");
    if (!documentDrive) throw new Error("Document library not found");
    return documentDrive.id;
  };

  const ensureParentFolder = async (accessToken, siteId, driveId, year) => {
    // First, ensure the Opportunity folder exists
    const opportunityFolderUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root:/${parentFolder}`;
    let opportunityFolderId;
    try {
      const response = await axios.get(opportunityFolderUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      opportunityFolderId = response.data.id;
    } catch (err) {
      if (err.response?.status === 404) {
        const createUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root/children`;
        const createResponse = await axios.post(
          createUrl,
          {
            name: parentFolder,
            folder: {},
            "@microsoft.graph.conflictBehavior": "rename",
          },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        opportunityFolderId = createResponse.data.id;
      } else {
        throw err;
      }
    }

    // Then, ensure the year folder exists under Opportunity
    const yearFolderUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${opportunityFolderId}/children/${year}`;
    try {
      const response = await axios.get(yearFolderUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data.id;
    } catch (err) {
      if (err.response?.status === 404) {
        const createUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${opportunityFolderId}/children`;
        const createResponse = await axios.post(
          createUrl,
          {
            name: year,
            folder: {},
            "@microsoft.graph.conflictBehavior": "rename",
          },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        return createResponse.data.id;
      } else {
        throw err;
      }
    }
  };

  const ensureEntityFolder = async (
    accessToken,
    siteId,
    driveId,
    parentFolderId,
    folderName,
    createdOn,
  ) => {
    // Format the folder name with the creation date
    const date = new Date(createdOn);
    const formattedDate = date.toISOString().split("T")[0]; // YYYY-MM-DD
    const formattedFolderName = `${formattedDate} - ${folderName}`;

    const checkUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentFolderId}/children/${formattedFolderName}`;
    try {
      const response = await axios.get(checkUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data.id;
    } catch (err) {
      if (err.response?.status === 404) {
        const createUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentFolderId}/children`;
        const createResponse = await axios.post(
          createUrl,
          {
            name: formattedFolderName,
            folder: {},
            "@microsoft.graph.conflictBehavior": "rename",
          },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        return createResponse.data.id;
      } else {
        throw err;
      }
    }
  };

  const fetchFolderContents = async (
    accessToken,
    siteId,
    driveId,
    folderId,
  ) => {
    const folderUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${folderId}/children`;
    const response = await axios.get(folderUrl, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    setFiles(response.data.value);
  };

  useEffect(() => {
    let isMounted = true;

    const initializeSharePointFolder = async () => {
      if (!isAuthenticated || accounts.length === 0) {
        try {
          await msalInstance.loginRedirect(loginRequest);
          setLoading(false); // Stop loading while redirecting
        } catch (err) {
          console.error("Error initiating login redirect:", err);
          if (isMounted) {
            setError(
              "Failed to initiate authentication. Please try logging in again.",
            );
            setLoading(false);
          }
        }
        return;
      }

      try {
        const account = accounts[0];
        const accessToken = await acquireTokenSilent(account);
        const config = await fetchSharePointConfig(accessToken);
        if (!isMounted) return;
        setSharePointConfig(config);

        const siteId = await getSiteId(accessToken, config);
        const driveId = await getDriveId(accessToken, siteId);

        // Get the year from createdOn prop
        const year = createdOn
          ? new Date(createdOn).getFullYear().toString()
          : new Date().getFullYear().toString();

        // Ensure the year folder under Opportunity
        const yearFolderId = await ensureParentFolder(
          accessToken,
          siteId,
          driveId,
          year,
        );

        // Use formatted folder name with date
        let folderName = sharePointFolderName || entityName;
        const entityFolderId = await ensureEntityFolder(
          accessToken,
          siteId,
          driveId,
          yearFolderId,
          folderName,
          createdOn || new Date(), // Use provided createdOn or current date
        );

        // Save the formatted folder name
        const formattedFolderName = createdOn
          ? `${new Date(createdOn).toISOString().split("T")[0]} - ${folderName}`
          : `${new Date().toISOString().split("T")[0]} - ${folderName}`;

        if (!sharePointFolderName) {
          const updatedEntity = await saveFolderName(
            entityId,
            formattedFolderName,
          );
          if (isMounted) setEntity(updatedEntity);
        }

        if (!isMounted) return;
        setCurrentFolderId(entityFolderId);
        setFolderPath([
          { name: year, id: yearFolderId },
          { name: formattedFolderName, id: entityFolderId },
        ]);
        await fetchFolderContents(accessToken, siteId, driveId, entityFolderId);

        setFolderCreated(true);
        setLoading(false);
      } catch (err) {
        console.error("Error in initializeSharePointFolder:", err);
        if (isMounted) {
          setError(
            "Failed to load SharePoint files: " +
              (err.response?.data?.error?.message || err.message),
          );
          setLoading(false);
        }
      }
    };

    initializeSharePointFolder();

    return () => {
      isMounted = false;
    };
  }, [
    isAuthenticated,
    accounts,
    entityId,
    entityName,
    sharePointFolderName,
    setEntity,
    entityType,
    msalInstance,
    createdOn, // Add createdOn to dependency array
  ]);

  const createSubfolder = async () => {
    if (!newFolderName) {
      alert("Please enter a folder name.");
      return;
    }

    try {
      const account = accounts[0];
      const accessToken = await acquireTokenSilent(account);
      const siteId = await getSiteId(accessToken, sharePointConfig);
      const driveId = await getDriveId(accessToken, siteId);
      const createUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${currentFolderId}/children`;

      const response = await axios.post(
        createUrl,
        {
          name: newFolderName,
          folder: {},
          "@microsoft.graph.conflictBehavior": "rename",
        },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      await fetchFolderContents(accessToken, siteId, driveId, currentFolderId);
      setNewFolderName("");
      setShowModal(false);
    } catch (err) {
      console.error("Error creating subfolder:", err.response?.data || err);
      setError(
        "Failed to create subfolder: " +
          (err.response?.data?.error?.message || err.message),
      );
    }
  };

  const uploadFile = async (file) => {
    try {
      const account = accounts[0];
      const accessToken = await acquireTokenSilent(account);
      const siteId = await getSiteId(accessToken, sharePointConfig);
      const driveId = await getDriveId(accessToken, siteId);
      const uploadUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${currentFolderId}:/${file.name}:/content`;

      const response = await axios.put(uploadUrl, file, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/octet-stream",
        },
      });
      await fetchFolderContents(accessToken, siteId, driveId, currentFolderId);
    } catch (err) {
      console.error("Error uploading file:", err.response?.data || err);
      setError(
        "Failed to upload file: " +
          (err.response?.data?.error?.message || err.message),
      );
    }
  };

  const handleFolderClick = async (folder) => {
    try {
      const account = accounts[0];
      const accessToken = await acquireTokenSilent(account);
      const siteId = await getSiteId(accessToken, sharePointConfig);
      const driveId = await getDriveId(accessToken, siteId);

      setCurrentFolderId(folder.id);
      setFolderPath([...folderPath, { name: folder.name, id: folder.id }]);
      await fetchFolderContents(accessToken, siteId, driveId, folder.id);
    } catch (err) {
      setError(
        "Failed to navigate to folder: " +
          (err.response?.data?.error?.message || err.message),
      );
    }
  };

  const handleBreadcrumbClick = async (folder, index) => {
    try {
      const account = accounts[0];
      const accessToken = await acquireTokenSilent(account);
      const siteId = await getSiteId(accessToken, sharePointConfig);
      const driveId = await getDriveId(accessToken, siteId);

      setCurrentFolderId(folder.id);
      setFolderPath(folderPath.slice(0, index + 1));
      await fetchFolderContents(accessToken, siteId, driveId, folder.id);
    } catch (err) {
      setError(
        "Failed to navigate back: " +
          (err.response?.data?.error?.message || err.message),
      );
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    for (const file of droppedFiles) {
      await uploadFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const getSharePointLink = () => {
    if (!sharePointConfig || !folderCreated) return "";
    const { tenant, sitePath } = sharePointConfig;
    const folderNames = folderPath.map((folder) => folder.name).join("/");
    return `https://${tenant}${sitePath}/${documentLibrary}/${parentFolder}/${folderNames}`;
  };

  if (loading) return <div className="text-center py-4">Loading...</div>;
  if (error)
    return <div className="text-red-600 text-center py-4">{error}</div>;

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">
        SharePoint Files for {entityName}
      </h2>
      {folderCreated && (
        <p className="text-sm text-gray-600 mb-4">
          Folder: {sharePointFolderName || "Creating..."}
        </p>
      )}

      {sharePointConfig && (
        <div className="mb-4">
          <a
            href={getSharePointLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline text-sm"
          >
            Open in SharePoint
          </a>
        </div>
      )}

      <div className="mb-4">
        {folderPath.map((folder, index) => (
          <span key={folder.id}>
            <button
              onClick={() => handleBreadcrumbClick(folder, index)}
              className="text-indigo-600 hover:underline"
            >
              {folder.name}
            </button>
            {index < folderPath.length - 1 && " > "}
          </span>
        ))}
      </div>

      <button
        onClick={() => setShowModal(true)}
        className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 mb-4"
      >
        Create Folder
      </button>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-gray-300 p-6 mb-4 text-center rounded-md bg-gray-50"
      >
        <p className="text-gray-500">Drag and drop files here to upload</p>
      </div>

      {files.length === 0 ? (
        <p className="text-gray-500">No files or folders found.</p>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Modified
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {files.map((file) => (
              <tr key={file.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {file.folder ? (
                    <button
                      onClick={() => handleFolderClick(file)}
                      className="text-indigo-600 hover:underline"
                    >
                      {file.name}
                    </button>
                  ) : (
                    file.name
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {file.folder ? "Folder" : "File"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(file.lastModifiedDateTime).toLocaleString("en-US", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                  {!file.folder && (
                    <button
                      onClick={() => window.open(file.webUrl, "_blank")}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      Download
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4">Create New Folder</h3>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="w-full p-2 border rounded-md mb-4"
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowModal(false)}
                className="bg-gray-300 px-4 py-2 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={createSubfolder}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunitySharePointFilesTab;
